import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, TrendingUp, RefreshCw } from 'lucide-react';

// API PROD
const OPPORTUNITIES_API_URL = 'https://api-ovh.alainponcelas.com/api/public/opportunities/active';

// Fallback mock data (utilisé uniquement si l'API échoue)
const MOCK_OPPORTUNITIES = [
  {
    id: "vietnam-ftse-2026",
    title: "Le Vietnam devient un pays émergent en septembre 2026",
    subtitle: "Reclassement FTSE : le Vietnam devient Émergent",
    accroche: "FTSE Russell a annoncé le reclassement prévu en septembre 2026. Les ETF passifs (VWO, SCHE) devront investir mécaniquement. Flux passifs estimés : plusieurs milliards USD.",
    these: "28 sociétés éligibles au reclassement, triées par score VALORAMAP. Disponibles maintenant, avant les flux mécaniques.",
    flag_emoji: "🇻🇳",
    stocks_count: 28,
    screener_filter: { country: "VN", sort: "valoramap_score", order: "desc", limit: 30, catalyst_ftse_eligible: true }
  },
  {
    id: "inde-rbi-pe-2026",
    title: "L'Inde décotée au moment où la Banque Centrale coupe les taux",
    subtitle: "Baisse de taux RBI + ratios P/E comprimés",
    accroche: "Les ratios Prix/Bénéfices (P/E) indiens ont corrigé sous leur moyenne 5 ans, juste au moment où la RBI entame un cycle d'assouplissement. L'opportunité est rare. Score macro VALORAMAP : 83%.",
    these: "Les meilleures sociétés indiennes aux valorisations les plus basses, scorées et investissables via ADR ou ETF.",
    flag_emoji: "🇮🇳",
    stocks_count: 2641,
    screener_filter: { country: "IN", sort: "valoramap_score", order: "desc", limit: 30 }
  },
  {
    id: "pologne-defense-eu-2026",
    title: "La Pologne : budget défense 4% PIB + fonds EU = catalyseur double",
    subtitle: "Défense record + fonds EU",
    accroche: "Premier budget OTAN d'Europe, décaissement NextGenEU en cours, relocalisation industrielle au profit de la Pologne. Actions disponibles directement sur la Bourse de Varsovie (WSE) via IBKR ou Degiro.",
    these: "Sociétés polonaises solides financièrement, disponibles en direct sur la Bourse de Varsovie.",
    flag_emoji: "🇵🇱",
    stocks_count: 587,
    screener_filter: { country: "PL", sort: "valoramap_score", order: "desc", limit: 30 }
  },
  {
    id: "coree-valueup-2026",
    title: "La Corée du Sud : programme Value-Up contre la décote historique",
    subtitle: "Réforme gouvernance + décote record",
    accroche: "Le gouvernement coréen impose un programme Value-Up aux chaebols pour réduire la décote historique vs Japon/US. Samsung, Hyundai et les autres doivent augmenter dividendes et rachats d'actions.",
    these: "Les sociétés coréennes les mieux positionnées pour bénéficier du programme Value-Up, scorées par VALORAMAP.",
    flag_emoji: "🇰🇷",
    stocks_count: 1478,
    screener_filter: { country: "KR", sort: "valoramap_score", order: "desc", limit: 30 }
  }
];

const OpportunitiesDashboard = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(OPPORTUNITIES_API_URL);
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      
      // L'API retourne { opportunities: [...] }
      const opps = data.opportunities || data;
      if (Array.isArray(opps) && opps.length > 0) {
        setOpportunities(opps);
      } else {
        // Fallback to mock if API returns empty
        console.warn('API returned empty, using mock data');
        setOpportunities(MOCK_OPPORTUNITIES);
      }
    } catch (err) {
      console.error('Failed to fetch opportunities:', err);
      setError('Utilisation des données de démonstration');
      setOpportunities(MOCK_OPPORTUNITIES);
    } finally {
      setLoading(false);
    }
  };

  const handleExplore = (opportunity) => {
    const { screener_filter } = opportunity;
    const params = new URLSearchParams();
    
    // Map country codes: API uses IN/PL/KR, screener uses NSE/WSE/KRX
    const countryMapping = { IN: 'NSE', PL: 'WSE', KR: 'KRX', VN: 'VN', ID: 'JK', PH: 'PSE' };
    const country = countryMapping[screener_filter.country] || screener_filter.country;
    
    if (country) params.set('country', country);
    if (screener_filter.sort) params.set('sort', screener_filter.sort);
    if (screener_filter.order) params.set('order', screener_filter.order);
    if (screener_filter.catalyst_ftse_eligible) params.set('catalyst_ftse_eligible', 'true');
    
    navigate(`/screener?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement des opportunités...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Opportunités du moment
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            4 thèses d'investissement sur les marchés émergents, analysées et scorées par VALORAMAP
          </p>
        </div>

        {/* Error notice (if using mock) */}
        {error && (
          <div className="mb-6 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm text-center">
            {error}
          </div>
        )}

        {/* Opportunities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {opportunities.map((opp) => (
            <Card 
              key={opp.id} 
              className="bg-white/5 border-white/10 hover:border-emerald-500/50 transition-all duration-200 overflow-hidden group"
              data-testid={`opportunity-card-${opp.id}`}
            >
              <CardContent className="p-6">
                {/* Flag + Subtitle */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{opp.flag_emoji}</span>
                  <span className="text-sm text-emerald-400 font-medium">{opp.subtitle}</span>
                </div>

                {/* Title */}
                <h2 className="text-xl font-bold text-white mb-3 leading-tight">
                  {opp.title}
                </h2>

                {/* Accroche */}
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {opp.accroche}
                </p>

                {/* These (investment thesis) */}
                <div className="bg-white/5 rounded-lg p-3 mb-4">
                  <p className="text-gray-300 text-sm italic">
                    "{opp.these}"
                  </p>
                </div>

                {/* Stocks count + CTA */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-400">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm">
                      {opp.stocks_count > 0 ? (
                        <>{opp.stocks_count} sociétés analysées</>
                      ) : (
                        <>Sociétés disponibles</>
                      )}
                    </span>
                  </div>
                  
                  <Button
                    onClick={() => handleExplore(opp)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 group-hover:gap-3 transition-all"
                    data-testid={`explore-btn-${opp.id}`}
                  >
                    Explorer
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Full screener link */}
        <div className="text-center">
          <button
            onClick={() => navigate('/screener')}
            className="text-gray-400 hover:text-emerald-400 transition-colors text-sm underline underline-offset-4"
            data-testid="full-screener-link"
          >
            Ouvrir le screener complet (6 500+ sociétés)
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpportunitiesDashboard;
